import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../auth/useUser';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { usePaddle } from '../paddle/PaddleContext';
import CreditIcon from '../components/ui/CreditIcon';
import SEO from '../components/SEO';

export function PricingPage() {
  const { loginWithRedirect, isAuthenticated, user } = useAuth0();
  const {
    credits,
    monthlyCredits,
    topupCredits,
    monthlyCreditsExpiresAt,
    refetchCredits, // unused now, but keeping destructure safe if preferred or just remove
    refetchUserInfo, // unused
    backendId
  } = useUser();

  const {
    isPro,
    hasProSubscription: _hasProSubscription, // keeping if needed elsewhere but prefer hook values
    subscriptionExpiresAt: _subscriptionExpiresAt,
    label: subscriptionLabel,
    isCancelled
  } = useSubscriptionStatus();

  // keeping local alias to avoid breaking rest of file that uses hasProSubscription
  const hasProSubscription = isPro;
  const subscriptionExpiresAt = _subscriptionExpiresAt;
  const { isReady, openSubscriptionCheckout, openCreditPackCheckout, getPricePreview } = usePaddle();

  const [prices, setPrices] = useState({
    pro: null,
    pack50: null,
    pack200: null
  });

  // Fetch dynamic prices
  useEffect(() => {
    if (!isReady) return;

    const fetchPrices = async () => {
      const proPriceId = import.meta.env.VITE_PADDLE_PRO_PRICE_ID;
      const pack50Id = import.meta.env.VITE_PADDLE_50_CREDITS_PRICE_ID;
      const pack200Id = import.meta.env.VITE_PADDLE_200_CREDITS_PRICE_ID;

      // Log IDs for debugging (Price IDs required, not Product IDs)
      console.log('Pricing IDs (Must be Price IDs):', { proPriceId, pack50Id, pack200Id });

      const items = [];
      if (proPriceId) items.push({ priceId: proPriceId, quantity: 1 });
      if (pack50Id) items.push({ priceId: pack50Id, quantity: 1 });
      if (pack200Id) items.push({ priceId: pack200Id, quantity: 1 });

      if (items.length === 0) {
        console.warn('No price IDs configured');
        return;
      }

      const response = await getPricePreview(items);

      if (response && response.data && response.data.details && response.data.details.lineItems) {
        const newPrices = { ...prices };

        response.data.details.lineItems.forEach(item => {
          // Use subtotal to show the price before tax/VAT is added
          const formattedPrice = item.formattedTotals.subtotal;
          const priceId = item.price.id;

          if (priceId === proPriceId) newPrices.pro = formattedPrice;
          if (priceId === pack50Id) newPrices.pack50 = formattedPrice;
          if (priceId === pack200Id) newPrices.pack200 = formattedPrice;
        });

        console.log('Fetched prices:', newPrices);
        setPrices(newPrices);
      }
    };

    fetchPrices();
  }, [isReady, getPricePreview]);



  const handleSubscribe = () => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: '/pricing' }
      });
      return;
    }

    // Use the backend internal UUID for Paddle tracking
    openSubscriptionCheckout(backendId, user?.email);
  };

  const handleBuyPack = (packPriceId) => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: '/pricing' }
      });
      return;
    }

    // Use the backend internal UUID for Paddle tracking
    openCreditPackCheckout(backendId, user?.email, packPriceId);
  };

  const pack200Id = import.meta.env.VITE_PADDLE_200_CREDITS_PRICE_ID;
  const pack50Id = import.meta.env.VITE_PADDLE_50_CREDITS_PRICE_ID;

  /* 
   * formatRenewalDate removed - now using useSubscriptionStatus hook logic 
   */

  return (
    <main className="flex-1 app-scroll">
      <SEO
        title="Pricing"
        description="AuraIcon pricing plans - Start free with 20 credits. Pro plan $12/mo for unlimited generations. Buy credit packs that never expire."
        canonical="/pricing"
      />
      <div className="container-page py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50">Pricing</h1>
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              Generate unlimited icons, pay only when you download
            </p>
          </div>

          {/* Compact Balance */}
          {isAuthenticated && (
            <div className="glass-soft p-3 mb-6 flex flex-wrap justify-between items-center gap-4 px-6">
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Your Balance:</span>
                <CreditIcon size="lg" />
                <span className="text-xl font-bold text-violet-600 dark:text-violet-400">{credits}</span>
                <span className="text-slate-500 text-xs">Total Credits</span>
              </div>

              <div className="flex gap-6 items-center">
                <div className="flex flex-col items-end">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly</span>
                  <div className="flex items-baseline gap-1">
                    <span className="font-bold text-slate-800 dark:text-slate-200">{monthlyCredits}</span>
                  </div>
                  {hasProSubscription && subscriptionExpiresAt && (
                    <span className={`text-[9px] font-medium whitespace-nowrap ${isCancelled ? 'text-red-500 dark:text-red-400' : 'text-violet-500 dark:text-violet-400'}`}>
                      {subscriptionLabel}
                    </span>
                  )}
                </div>
                <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
                <div className="flex flex-col items-end">
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest">Top-up</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">{topupCredits}</span>
                  <span className="text-[9px] text-emerald-500">Never expires</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
            {/* Free Tier */}
            <div className="glass p-4 flex flex-col">
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-1">Free</h3>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">$0</p>
              <ul className="space-y-2 mb-4 flex-1 text-sm">
                <PricingFeature text="20 starter credits" />
                <PricingFeature text="10 gen/day limit" />
                <PricingFeature text="5 credits per download" />
              </ul>
              {!isAuthenticated ? (
                <button onClick={() => loginWithRedirect()} className="w-full btn-secondary py-2 text-sm">
                  Sign up free
                </button>
              ) : (
                <div className={`text-center py-2 text-xs rounded-lg ${!hasProSubscription ? 'bg-slate-200 dark:bg-slate-800 font-semibold' : 'text-slate-400'}`}>
                  {hasProSubscription ? 'Included' : 'Current Plan'}
                </div>
              )}
            </div>

            {/* Pro Tier */}
            <div className="glass p-4 relative border-violet-400/30 bg-violet-500/10 flex flex-col">
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 badge-brand text-[9px] py-0.5 px-2">
                Recommended
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-1 mt-1">Pro</h3>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                {prices.pro ? (
                  <>
                    {prices.pro}<span className="text-sm font-normal text-slate-500">/mo</span>
                  </>
                ) : (
                  <span className="h-8 w-16 bg-slate-200 dark:bg-slate-700 animate-pulse inline-block rounded" />
                )}
              </p>
              <ul className="space-y-2 mb-4 flex-1 text-sm">
                <PricingFeature text="Unlimited generations" premium />
                <PricingFeature text="100 credits/month" premium />
                <PricingFeature text="Priority support" premium />
              </ul>
              {hasProSubscription ? (
                <div className="text-center text-emerald-500 py-2 text-xs font-semibold bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  Active
                </div>
              ) : (
                <button
                  onClick={handleSubscribe}
                  disabled={!isReady}
                  className="w-full btn-primary py-2 text-sm font-bold"
                >
                  {isAuthenticated ? 'Upgrade' : 'Get Started'}
                </button>
              )}
            </div>

            {/* Top-ups */}
            <div className="glass p-4 flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Top-up</h3>
                <span className="text-[9px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded-full font-bold uppercase">Never expire</span>
              </div>
              <div className="space-y-3 flex-1">
                <CreditPackCard
                  credits={50}
                  price={prices.pack50}
                  fallbackPrice="$9.99"
                  onClick={() => handleBuyPack(pack50Id)}
                  disabled={!isReady}
                  compact
                />
                <CreditPackCard
                  credits={200}
                  price={prices.pack200}
                  fallbackPrice="$29.99"
                  onClick={() => handleBuyPack(pack200Id)}
                  disabled={!isReady}
                  recommended
                  compact
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-3 text-center">One-time purchase</p>
            </div>
          </div>

          {/* Compressed FAQ Footer */}
          <div className="mt-10 pt-8 border-t border-slate-200 dark:border-slate-800">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <FaqSmall
                q="How do credits work?"
                a="Used on download. 5 credits per icon."
              />
              <FaqSmall
                q="Do they expire?"
                a="Monthly expire, Top-ups are permanent."
              />
              <FaqSmall
                q="Priority?"
                a="Monthly used first, then Top-up."
              />
              <FaqSmall
                q="Refunds?"
                a="14-day refund window for all purchases."
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function CreditPackCard({ credits, price, fallbackPrice, onClick, disabled, recommended, compact }) {
  const displayPrice = price || (disabled ? fallbackPrice : null);

  if (compact) {
    return (
      <div className={`glass-soft p-3 flex items-center justify-between ${recommended ? 'border-violet-400/50 bg-violet-500/5' : ''}`}>
        <div className="flex items-center gap-3">
          <CreditIcon size="md" />
          <div>
            <span className="text-lg font-bold text-slate-900 dark:text-slate-50">{credits}</span>
            <span className="text-xs text-slate-500 ml-1">credits</span>
          </div>
          {recommended && (
            <span className="text-[8px] uppercase tracking-widest font-bold text-violet-400">Best Value</span>
          )}
        </div>
        <button
          onClick={onClick}
          disabled={disabled || !price}
          className={`py-1.5 px-4 rounded-lg text-xs font-semibold transition-all min-w-[70px] cursor-pointer ${recommended
            ? 'bg-violet-600 hover:bg-violet-700 text-white'
            : 'bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-50'
            } ${!price ? 'animate-pulse opacity-70' : ''}`}
        >
          {price || <span className="opacity-0">---</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`glass p-5 text-center flex flex-col items-center ${recommended ? 'border-violet-400/50 bg-violet-500/5' : ''}`}>
      {recommended && (
        <span className="text-[10px] uppercase tracking-widest font-bold text-violet-400 mb-2">Best Value</span>
      )}
      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50">{credits} Credits</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{Math.floor(credits / 5)} Downloads</p>
      <div className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-4">{price}</div>
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full py-2 rounded-lg font-semibold transition-all cursor-pointer ${recommended
          ? 'bg-violet-600 hover:bg-violet-700 text-white'
          : 'bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-50'
          }`}
      >
        Buy Now
      </button>
    </div>
  );
}

function CheckIcon({ className = 'text-emerald-600 dark:text-emerald-300' }) {
  return (
    <svg className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PricingFeature({ text, premium }) {
  return (
    <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
      <CheckIcon className={premium ? "text-violet-500" : "text-emerald-500"} />
      <span>{text}</span>
    </li>
  );
}

function FaqSmall({ q, a }) {
  return (
    <div>
      <h4 className="text-[11px] font-bold text-slate-900 dark:text-slate-100 uppercase tracking-widest mb-1">{q}</h4>
      <p className="text-xs text-slate-500 dark:text-slate-400">{a}</p>
    </div>
  );
}

export default PricingPage;
